<template>
  <!-- ── Hero ──────────────────────────────────────────── -->
  <section
    class="hero-section"
    role="region"
    aria-label="Golden Key Imobiliária"
  >
    <div class="hero-static">
      <Transition name="skeleton-fade">
        <div
          v-if="!heroImageLoaded"
          class="carousel-skeleton"
          aria-hidden="true"
        >
          <div class="skeleton-shimmer"></div>
        </div>
      </Transition>
      <img
        v-if="heroImageUrl"
        :src="heroImageUrl"
        class="hero-img"
        alt="Golden Key"
        loading="eager"
        @load="heroImageLoaded = true"
        @error="heroImageLoaded = true"
      />
      <div class="slide-gradient" aria-hidden="true"></div>
      <div class="hero-copy">
        <p>GOLDEN KEY</p>
        <h1>Encontre casa e carro em Maputo.<br />Imóveis, alojamento e rent-a-car</h1>
      </div>
    </div>

    <!-- Motor de busca medido no frame 1:3 do Figma. -->
    <div class="search-bar-anchor">
      <div class="search-panel">
        <nav class="hero-product-tabs" aria-label="Escolher serviço">
          <button v-for="product in productTabs" :key="product.value" type="button" class="hero-product-tab" :class="{ 'is-active': activeProduct === product.value }" @click="activeProduct = product.value">{{ product.label }}</button>
        </nav>

        <div class="search-fields-grid">
          <label v-if="activeProduct === 'property'" class="search-field">
            <span class="search-field__label">Operação</span>
            <span class="search-control-wrap">
              <select v-model="filtros.objective" class="search-control">
                <option
                  v-for="option in objectiveOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
              <span class="search-control-chevron" aria-hidden="true">▾</span>
            </span>
          </label>

          <label v-else class="search-field">
            <span class="search-field__label">{{ activeProduct === 'accommodation' ? 'Tipo de alojamento' : 'Categoria' }}</span>
            <span class="search-control-wrap">
              <select v-model="filtros.category" class="search-control">
                <option value="">Todos</option>
                <option v-for="option in activeCatalogOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
              </select>
              <span class="search-control-chevron" aria-hidden="true">▾</span>
            </span>
          </label>

          <label class="search-field search-field--zone">
            <span class="search-field__label">Zona</span>
            <input
              v-model="filtros.localizacao"
              id="sf-location"
              class="search-control"
              placeholder="Bairro, cidade ou referência"
              type="text"
              autocomplete="off"
            />
          </label>

          <label class="search-field">
            <span class="search-field__label">{{ activeProduct === 'property' ? 'Tipologia' : activeProduct === 'accommodation' ? 'Hóspedes' : 'Serviço' }}</span>
            <span class="search-control-wrap">
              <select v-if="activeProduct === 'property'" v-model="filtros.quartos" class="search-control">
                <option v-for="option in tipologiaOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
              <select v-else-if="activeProduct === 'accommodation'" v-model="filtros.guests" class="search-control">
                <option value="">Todos</option><option v-for="value in [1,2,4,6,8]" :key="value" :value="value">{{ value }}+</option>
              </select>
              <select v-else v-model="filtros.service" class="search-control">
                <option value="">Todos</option><option value="self_drive">Sem motorista</option><option value="with_driver">Com motorista</option>
              </select>
              <span class="search-control-chevron" aria-hidden="true">▾</span>
            </span>
          </label>

          <label class="search-field">
            <span class="search-field__label">Preço até</span>
            <span class="search-control-wrap">
              <select v-model="filtros.maxPrice" class="search-control">
                <option v-for="option in priceOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
              <span class="search-control-chevron" aria-hidden="true">▾</span>
            </span>
          </label>

          <button class="search-cta" type="button" @click="handleSearch">
            Pesquisar
          </button>
        </div>
      </div>
    </div>
  </section>
  <!-- Botão para anunciar neste espaço -->
  <div class="advertise-container">
    <q-btn
      size="sm"
      padding="xs"
      rounded
      label="Anuncie neste espaço"
      class="advertise-btn"
      @click="irParaSuporte"
    />
  </div>
  <CarouselAd
    class="home-top-ad"
    variant="horizontal"
    placement-key="home_top_banner"
  />

  <!-- ── Properties grid ───────────────────────────────── -->
  <section class="properties-section">
    <div class="home-container">
        <div class="home-section-heading">
          <h2>{{ homePropertiesUsingFallback ? "Imóveis disponíveis" : "Imóveis em destaque" }}</h2>
          <div class="featured-heading-actions">
            <div class="featured-slider-controls" aria-label="Navegar pelos imóveis">
              <button type="button" aria-label="Imóveis anteriores" @click.stop="scrollFeatured(-1)">
                <span aria-hidden="true">←</span>
              </button>
              <button type="button" aria-label="Próximos imóveis" @click.stop="scrollFeatured(1)">
                <span aria-hidden="true">→</span>
              </button>
            </div>
            <button class="see-all-btn" @click="router.push('/imoveis-search')">
              Ver todos os imóveis
              <span aria-hidden="true">→</span>
            </button>
          </div>
        </div>

        <!-- Skeleton -->
        <div v-if="loader" class="properties-grid">
          <div
            v-for="n in 4"
            :key="'skeleton-' + n"
            class="property-grid-item"
          >
            <q-card flat bordered class="imovel-card">
              <q-skeleton class="property-card__skeleton-image" square />
              <q-card-section>
                <q-skeleton type="text" width="40%" class="q-mb-xs" />
                <q-skeleton type="text" width="80%" class="q-mb-xs" />
                <q-skeleton type="text" width="60%" class="q-mb-md" />
                <div class="row q-gutter-md q-mb-md">
                  <q-skeleton type="text" width="30px" />
                  <q-skeleton type="text" width="30px" />
                  <q-skeleton type="text" width="40px" />
                  <q-skeleton type="text" width="30px" />
                </div>
                <q-separator class="q-my-md" />
                <div class="row items-center justify-between">
                  <q-skeleton type="text" width="50%" />
                  <q-skeleton type="QBtn" />
                </div>
              </q-card-section>
            </q-card>
          </div>
        </div>

        <!-- Featured properties -->
        <div
          v-else
          ref="featuredTrack"
          class="properties-grid properties-grid--carousel"
          :data-featured-index="featuredIndex"
        >
          <div
            v-for="(imovel, index) in featuredHomeProperties"
            :key="imovel.slug || index"
            class="property-grid-item"
          >
            <article
              class="imovel-card"
              tabindex="0"
              @click="verDetalhes(imovel)"
              @keydown.enter="verDetalhes(imovel)"
            >
              <div class="image-container">
                <q-img
                  :src="imovel.primary_image_url"
                  :ratio="4 / 3"
                  class="property-card__image"
                >
                  <template v-slot:loading>
                    <q-skeleton class="fit" />
                  </template>
                </q-img>
                <span class="tipo-negocio-badge">
                  {{ imovel.objective }}
                </span>
                <span
                  v-if="imovel.promotion?.badge_label || imovel.promotion?.editorial_featured"
                  class="featured-badge"
                >
                  <i aria-hidden="true"></i>
                  {{ imovel.promotion?.badge_label || 'Destaque editorial' }}
                </span>
                <span v-if="getPhotoCount(imovel)" class="property-card__photos">
                  {{ getPhotoCount(imovel) }} fotos
                </span>
              </div>

              <div class="property-card__content">
                <div class="property-card__price-row">
                  <div>
                    <strong>{{ formatPrice(imovel.price, imovel.currency) }}<small v-if="imovel.objective !== 'Venda'">/mês</small></strong>
                  </div>
                  <span class="property-card__favorite" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" width="19" height="19">
                      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" />
                    </svg>
                  </span>
                </div>

                <h3 class="property-card__title">
                  {{ imovel.title }}
                </h3>
                <p class="property-card__location">{{ imovel.location }}</p>

                <div class="property-card__facts">
                  <span v-if="imovel.bedroom_amount">{{ imovel.bedroom_amount }} quartos</span>
                  <span v-if="imovel.bathroom_amount">{{ imovel.bathroom_amount }} WC</span>
                  <span v-if="imovel.area">{{ imovel.area }} m²</span>
                  <span v-if="imovel.garage_spaces">{{ imovel.garage_spaces }} garagem</span>
                </div>

                <small v-if="imovel.updated_at || imovel.created_at" class="property-card__updated">
                  Atualizado {{ formatUpdatedAt(imovel.updated_at || imovel.created_at) }}
                </small>
              </div>
            </article>
          </div>
        </div>
        <div v-if="!loader && !featuredHomeProperties.length" class="properties-empty-state">
          {{ homePropertiesError || "Não existem imóveis disponíveis neste momento." }}
        </div>

    </div>
  </section>

  <CarouselAd
    class="home-carousel-placement"
    variant="horizontal"
    placement-key="home_featured_after"
  />

    <section class="home-catalog-section">
      <div class="home-container">
        <div class="home-catalog-heading">
          <h2>Alojamento por noite</h2>
          <div class="featured-heading-actions">
            <div class="featured-slider-controls" aria-label="Navegar pelos alojamentos em destaque">
              <button type="button" aria-label="Alojamentos anteriores" @click.stop="scrollAccommodation(-1)">←</button>
              <button type="button" aria-label="Próximos alojamentos" @click.stop="scrollAccommodation(1)">→</button>
            </div>
            <button type="button" @click="router.push('/alojamentos')">Ver alojamento →</button>
          </div>
        </div>
        <div
          v-if="!homeAccommodationLoading && homeAccommodationItems.length"
          ref="accommodationTrack"
          class="home-catalog-grid home-catalog-grid--carousel"
          :data-catalog-index="accommodationIndex"
        >
          <article
            v-for="item in homeAccommodationItems"
            :key="item.slug"
            class="home-catalog-card"
            tabindex="0"
            @click="router.push(`/alojamentos/${item.slug}`)"
            @keydown.enter="router.push(`/alojamentos/${item.slug}`)"
          >
            <div class="home-catalog-card__image">
              <img :src="item.image" :alt="item.title" loading="lazy" />
              <span>{{ item.badge }}</span>
              <span v-if="item.promotionLabel" class="home-catalog-card__promotion">{{ item.promotionLabel }}</span>
            </div>
            <div class="home-catalog-card__content">
              <div class="home-catalog-card__price">
                <strong>{{ item.price }} <small>/ noite</small></strong>
                <span class="property-card__favorite" aria-hidden="true">♡</span>
              </div>
              <h3>{{ item.title }}</h3>
              <p class="home-catalog-card__location">{{ item.location }}</p>
              <div class="property-card__facts">
                <span v-for="fact in item.facts.split(' · ')" :key="fact">{{ fact }}</span>
              </div>
            </div>
          </article>
        </div>
        <div v-else class="home-catalog-state">
          <q-spinner-dots v-if="homeAccommodationLoading" color="primary" size="38px" />
          <template v-else><strong>{{ homeAccommodationError ? "Não foi possível carregar os alojamentos." : "Ainda não há alojamentos em destaque." }}</strong><span v-if="homeAccommodationError">{{ homeAccommodationError }}</span></template>
        </div>
      </div>
    </section>

    <section class="home-catalog-section">
      <div class="home-container">
        <div class="home-catalog-heading">
          <h2>Rent-a-Car</h2>
          <div class="featured-heading-actions">
            <div class="featured-slider-controls" aria-label="Navegar pelas viaturas em destaque">
              <button type="button" aria-label="Viaturas anteriores" @click.stop="scrollRent(-1)">←</button>
              <button type="button" aria-label="Próximas viaturas" @click.stop="scrollRent(1)">→</button>
            </div>
            <button type="button" @click="router.push('/rent-a-car')">Ver viaturas →</button>
          </div>
        </div>
        <div
          v-if="!homeRentLoading && homeRentItems.length"
          ref="rentTrack"
          class="home-catalog-grid home-catalog-grid--carousel"
          :data-catalog-index="rentIndex"
        >
          <article
            v-for="item in homeRentItems"
            :key="item.slug"
            class="home-catalog-card"
            tabindex="0"
            @click="router.push(`/rent-a-car/${item.slug}`)"
            @keydown.enter="router.push(`/rent-a-car/${item.slug}`)"
          >
            <div class="home-catalog-card__image">
              <img :src="item.image" :alt="item.title" loading="lazy" />
              <span>{{ item.badge }}</span>
              <span v-if="item.promotionLabel" class="home-catalog-card__promotion">{{ item.promotionLabel }}</span>
            </div>
            <div class="home-catalog-card__content">
              <div class="home-catalog-card__price">
                <strong>{{ item.price }} <small>/ dia</small></strong>
                <span class="property-card__favorite" aria-hidden="true">♡</span>
              </div>
              <h3>{{ item.title }}</h3>
              <p class="home-catalog-card__location">{{ item.location }}</p>
              <div class="property-card__facts">
                <span v-for="fact in item.facts.split(' · ')" :key="fact">{{ fact }}</span>
              </div>
            </div>
          </article>
        </div>
        <div v-else class="home-catalog-state">
          <q-spinner-dots v-if="homeRentLoading" color="primary" size="38px" />
          <template v-else><strong>{{ homeRentError ? "Não foi possível carregar as viaturas." : "Ainda não há viaturas em destaque." }}</strong><span v-if="homeRentError">{{ homeRentError }}</span></template>
        </div>
      </div>
    </section>

    <!-- ── Why Us ────────────────────────────────────────── -->
    <section class="why-section">
      <div class="why-container">
        <div class="why-header">
          <h2 class="why-title">Porque escolher-nos</h2>
          <p class="why-sub">
            Mais do que um portal — somos o seu parceiro de confiança em cada
            etapa da jornada.
          </p>
        </div>

        <div class="why-grid">
          <div
            class="why-card"
            v-for="item in whyItems"
            :key="item.title"
            :style="{ '--accent': item.color }"
          >
            <div class="why-icon">
              <svg
                :viewBox="item.viewBox"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
                stroke-linejoin="round"
                width="26"
                height="26"
                aria-hidden="true"
              >
                <path v-for="(d, di) in item.paths" :key="di" :d="d" />
              </svg>
            </div>
            <h3 class="why-card-title">{{ item.title }}</h3>
            <p class="why-card-body">{{ item.body }}</p>
          </div>
        </div>
      </div>
    </section>
</template>

<script setup>
import { computed, ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useHead } from "@unhead/vue";
import apiClient from "@/config/api";
import CarouselAd from "@/components/catalog/CarouselAd.vue";

const router = useRouter();
const drawer = ref(false);
const featuredTrack = ref(null);
const featuredIndex = ref(0);
const accommodationTrack = ref(null);
const rentTrack = ref(null);
const accommodationIndex = ref(0);
const rentIndex = ref(0);

const homeAccommodationItems = ref([]);
const homeRentItems = ref([]);
const homeAccommodationLoading = ref(true);
const homeRentLoading = ref(true);
const homeAccommodationError = ref("");
const homeRentError = ref("");

const irParaSuporte = () => {
  drawer.value = false;
  localStorage.setItem("supportSubject", "anunciar");
  router.replace("/suporte");
};

// ── SEO ──────────────────────────────────────────────────
useHead({
  title: "Golden key - Sua imobiliaria de confiança",
  meta: [
    {
      name: "keywords",
      content:
        "imóveis Maputo, casas venda Moçambique, apartamentos alugar Maputo, propriedades Moçambique, imobiliária Maputo, terrenos venda, casas luxo Maputo, apartamentos modernos",
    },
    {
      name: "description",
      content:
        "Sua imobiliária de confiança. Encontre o imóvel dos seus sonhos com as melhores condições do mercado.",
    },
    {
      property: "og:title",
      content: "Golden key - Sua imobiliaria de confiança",
    },
    {
      property: "og:description",
      content:
        "Sua imobiliária de confiança. Encontre o imóvel dos seus sonhos com as melhores condições do mercado.",
    },
    {
      property: "og:image",
      content:
        "https://amzn-s3-goldenkey.s3.us-east-2.amazonaws.com/properties/icon2.png",
    },
    { name: "geo.region", content: "MZ-MPM" },
    { name: "geo.placename", content: "Maputo" },
    { name: "language", content: "Portuguese" },
    { property: "og:locale", content: "pt_MZ" },
  ],
  link: [
    { rel: "canonical", href: "https://goldenkey.co.mz" },
    { rel: "canonical", href: "https://portal.goldenkey.co.mz" },
  ],
});

// ── Hero ──────────────────────────────────────────────────
const heroImageLoaded = ref(false);

// ── Ads Carousel ─────────────────────────────────────────
// ── Search ────────────────────────────────────────────────
const filtros = ref({
  objective: 3,
  localizacao: "",
  quartos: 2,
  maxPrice: 100000,
  category: "",
  guests: "",
  service: "",
});
const activeProduct = ref('property');
const productTabs = [
  { value: 'property', label: 'Imóveis' },
  { value: 'accommodation', label: 'Alojamento' },
  { value: 'rent', label: 'Rent-a-Car' },
];
const accommodationSearchOptions = ref([]);
const rentalSearchOptions = ref([]);
const activeCatalogOptions = computed(() => activeProduct.value === 'accommodation' ? accommodationSearchOptions.value : rentalSearchOptions.value);

const objectiveOptions = ref([
  { label: "Arrendar", value: 3 },
  { label: "Comprar", value: 1 },
]);
const tipologiaOptions = [1, 2, 3, 4, 5].map((value) => ({
  value,
  label: `T${value} ou mais`,
}));
const priceOptions = [50000, 100000, 250000, 500000, 1000000, 5000000].map(
  (value) => ({
    value,
    label: `${new Intl.NumberFormat("pt-PT").format(value)} MZN`,
  }),
);

async function fetchSearchOptions() {
  try {
    const [{ data }, accommodationResponse, rentalResponse] = await Promise.all([
      apiClient.get("/properties/options"),
      apiClient.get('/accommodations/options'),
      apiClient.get('/rental-vehicles/options'),
    ]);
    const objectives = data?.data?.objectives;
    if (!Array.isArray(objectives) || !objectives.length) return;

    objectiveOptions.value = objectives.map((option) => ({
      label: option.name,
      value: option.id,
    }));
    const rentOption = objectiveOptions.value.find((option) =>
      /arrend|alug/i.test(option.label),
    );
    filtros.value.objective = rentOption?.value ?? objectiveOptions.value[0].value;
    accommodationSearchOptions.value = (accommodationResponse.data?.data?.accommodation_types || []).map((option) => ({ label: option.name, value: option.id }));
    rentalSearchOptions.value = (rentalResponse.data?.data?.vehicle_categories || []).map((option) => ({ label: option.name, value: option.id }));
  } catch {
    /* Os valores de fallback mantêm o motor utilizável sem bloquear a home. */
  }
}

const handleSearch = () => {
  if (activeProduct.value === 'accommodation') {
    router.push({ path: '/alojamentos', query: {
      location: filtros.value.localizacao || undefined,
      accommodation_type_id: filtros.value.category || undefined,
      guests: filtros.value.guests || undefined,
      max_price: filtros.value.maxPrice || undefined,
    } });
    return;
  }
  if (activeProduct.value === 'rent') {
    router.push({ path: '/rent-a-car', query: {
      location: filtros.value.localizacao || undefined,
      vehicle_category_id: filtros.value.category || undefined,
      service_type: filtros.value.service || undefined,
      max_price: filtros.value.maxPrice || undefined,
    } });
    return;
  }
  router.push({
    name: "imoveis-search",
    query: {
      objective: filtros.value.objective || undefined,
      location: filtros.value.localizacao || undefined,
      bedroom_amount: filtros.value.quartos || undefined,
      max_price: filtros.value.maxPrice || undefined,
    },
  });
};

// ── Properties list ───────────────────────────────────────
const loader = ref(false);
const imoveis = ref([]);
const homePropertiesUsingFallback = ref(false);
const homePropertiesError = ref("");
const featuredHomeProperties = computed(() => imoveis.value);
const heroImageUrl = computed(() => imoveis.value[0]?.primary_image_url || "");

const scrollFeatured = (direction) => {
  const track = document.querySelector(".properties-grid--carousel") || featuredTrack.value;
  if (!track) return;
  const firstCard = track.querySelector(".property-grid-item");
  const gap = Number.parseFloat(window.getComputedStyle(track).columnGap) || 20;
  const cardWidth = firstCard?.getBoundingClientRect().width || track.clientWidth;
  const visibleCards = window.innerWidth >= 1100 ? 4 : window.innerWidth >= 700 ? 2 : 1;
  const lastIndex = Math.max(0, featuredHomeProperties.value.length - visibleCards);
  featuredIndex.value = Math.min(lastIndex, Math.max(0, featuredIndex.value + direction * visibleCards));
  track.scrollTo({ left: featuredIndex.value * (cardWidth + gap), behavior: "smooth" });
};

const scrollHomeCatalog = (track, index, direction, total) => {
  const element = track.value;
  if (!element) return;
  const firstCard = element.querySelector(".home-catalog-card");
  const gap = Number.parseFloat(window.getComputedStyle(element).columnGap) || 20;
  const cardWidth = firstCard?.getBoundingClientRect().width || element.clientWidth;
  const visibleCards = window.innerWidth >= 1025 ? 4 : window.innerWidth >= 601 ? 2 : 1;
  const lastIndex = Math.max(0, total - visibleCards);
  index.value = Math.min(lastIndex, Math.max(0, index.value + direction * visibleCards));
  element.scrollTo({ left: index.value * (cardWidth + gap), behavior: "smooth" });
};

const scrollAccommodation = (direction) =>
  scrollHomeCatalog(accommodationTrack, accommodationIndex, direction, homeAccommodationItems.value.length);
const scrollRent = (direction) =>
  scrollHomeCatalog(rentTrack, rentIndex, direction, homeRentItems.value.length);

const catalogPrice = (value, currency = "MZN") =>
  value == null ? "Sob consulta" : `${Number(value).toLocaleString("pt-MZ")} ${currency || "MZN"}`;

async function fetchHomeAccommodations() {
  homeAccommodationLoading.value = true;
  homeAccommodationError.value = "";
  try {
    const response = await apiClient.get("/accommodations/featured");
    const items = response.data?.data || [];
    homeAccommodationItems.value = items.map((item) => ({
      slug: item.slug,
      title: item.title,
      location: item.location,
      price: catalogPrice(item.price, item.currency),
      badge: item.type?.name || "Alojamento",
      promotionLabel: item.promotion?.badge_label || (item.promotion?.editorial_featured ? 'Destaque editorial' : ''),
      facts: `${item.capacity || 0} hóspedes · ${item.bedroom_amount || 0} quartos · ${item.bathroom_amount || 0} banhos`,
      image: item.primary_image_url || "https://placehold.co/900x600?text=Golden+Key",
    }));
  } catch (requestError) {
    homeAccommodationItems.value = [];
    homeAccommodationError.value = requestError.response?.data?.message || "Verifique a ligação ao servidor.";
  } finally {
    homeAccommodationLoading.value = false;
  }
}

async function fetchHomeVehicles() {
  homeRentLoading.value = true;
  homeRentError.value = "";
  try {
    const response = await apiClient.get("/rental-vehicles/featured");
    const items = response.data?.data || [];
    homeRentItems.value = items.map((item) => {
      const services = (item.service_options || []).map((service) => service.service_type === "with_driver" ? "Com motorista" : "Sem motorista");
      return {
        slug: item.slug,
        title: item.title,
        location: item.location,
        price: catalogPrice(item.price, item.currency),
        badge: item.category?.name || "Viatura",
        promotionLabel: item.promotion?.badge_label || (item.promotion?.editorial_featured ? 'Destaque editorial' : ''),
        facts: [item.transmission === "automatic" ? "Automática" : "Manual", `${item.seats || 0} lugares`, ...services].join(" · "),
        image: item.primary_image_url || "https://placehold.co/900x600?text=Golden+Key",
      };
    });
  } catch (requestError) {
    homeRentItems.value = [];
    homeRentError.value = requestError.response?.data?.message || "Verifique a ligação ao servidor.";
  } finally {
    homeRentLoading.value = false;
  }
}

function fetchHomeCatalogs() {
  fetchHomeAccommodations();
  fetchHomeVehicles();
}

async function fetchFeatured() {
  loader.value = true;
  homePropertiesError.value = "";
  homePropertiesUsingFallback.value = false;
  try {
    const { data } = await apiClient.get("/properties/featured");
    const featured = Array.isArray(data)
      ? data
      : Array.isArray(data.data)
        ? data.data
        : [];

    if (featured.length) {
      imoveis.value = featured;
      return;
    }
  } catch (requestError) {
    console.error("Error loading featured properties:", requestError);
  }

  try {
    const { data } = await apiClient.get("/properties", {
      params: { per_page: 8, page: 1, sort: "recommended" },
    });
    const properties = data.data?.data;
    if (!Array.isArray(properties)) {
      throw new Error("A resposta da lista pública de imóveis é inválida.");
    }
    imoveis.value = properties;
    homePropertiesUsingFallback.value = true;
  } catch (requestError) {
    console.error("Error loading public properties:", requestError);
    imoveis.value = [];
    homePropertiesError.value =
      "Não foi possível carregar os imóveis. Tente novamente mais tarde.";
  } finally {
    loader.value = false;
    if (!imoveis.value.length) heroImageLoaded.value = true;
  }
}

const formatPrice = (price, currency) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
  }).format(price);
};

const getPhotoCount = (imovel) =>
  imovel.image_count || imovel.images?.length || imovel.property_images?.length || 0;

const formatUpdatedAt = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("pt-PT", {
    day: "2-digit",
    month: "short",
  }).format(date);
};

const verDetalhes = (imovel) => {
  console.log("Ver detalhes do imóvel:", imovel);
  router.push({ name: "imovel-details", params: { slug: imovel.slug } });
};

// ── Why Us ────────────────────────────────────────────────
const whyItems = [
  {
    title: "Experiência Comprovada",
    body: "Anos de presença no mercado imobiliário moçambicano com uma equipa especializada que conhece cada bairro, cada oportunidade.",
    color: "#027BE3",
    viewBox: "0 0 24 24",
    paths: ["M12 2L2 7l10 5 10-5-10-5z", "M2 17l10 5 10-5", "M2 12l10 5 10-5"],
  },
  {
    title: "Portfólio Exclusivo",
    body: "Centenas de imóveis selecionados — apartamentos modernos, moradias de luxo, terrenos e espaços comerciais em todo o país.",
    color: "#EBA301",
    viewBox: "0 0 24 24",
    paths: ["M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z", "M9 22V12h6v10"],
  },
  {
    title: "Transparência Total",
    body: "Processos claros, documentação segura e acompanhamento em cada fase — da visita ao contrato, sem surpresas.",
    color: "#10b981",
    viewBox: "0 0 24 24",
    paths: ["M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z", "M9 12l2 2 4-4"],
  },
  {
    title: "Suporte Dedicado",
    body: "A nossa equipa está disponível para responder às suas dúvidas e guiá-lo nas melhores decisões, sempre ao seu ritmo.",
    color: "#8b5cf6",
    viewBox: "0 0 24 24",
    paths: [
      "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2",
      "M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
      "M23 21v-2a4 4 0 0 0-3-3.87",
      "M16 3.13a4 4 0 0 1 0 7.75",
    ],
  },
];

onMounted(() => {
  fetchFeatured();
  fetchHomeCatalogs();
  fetchSearchOptions();
});
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Karla:wght@400;600;700&family=Sora:wght@700&display=swap");

/* ══════════════════════════════════════════════════════
   Hero / Carousel
══════════════════════════════════════════════════════ */
.hero-section {
  position: relative;
  width: 100%;
}

/* ── Static hero ────────────────────────── */
.hero-static {
  position: relative;
  width: 100%;
  height: 420px;
  background: #C9C2B6;
  overflow: hidden;
}

.hero-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

.slide-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(8, 12, 24, 0.88) 0%,
    rgba(8, 12, 24, 0.45) 30%,
    rgba(8, 12, 24, 0.12) 60%,
    transparent 100%
  );
  pointer-events: none;
}

.hero-copy {
  position: absolute;
  z-index: 3;
  top: 104px;
  left: 50%;
  width: min(calc(100% - 96px), 980px);
  color: #fff;
  text-align: center;
  transform: translateX(-50%);
}

.hero-copy p { margin: 0 0 12px; font-size: 11px; font-weight: 700; letter-spacing: .18em; }
.hero-copy h1 { margin: 0; font-size: clamp(30px, 3vw, 48px); line-height: 1.12; font-weight: 650; letter-spacing: -.025em; text-shadow: 0 2px 18px rgba(0,0,0,.32); }

/* ── Hero skeleton ──────────────────────── */
.carousel-skeleton {
  position: absolute;
  inset: 0;
  z-index: 2;
  background: linear-gradient(135deg, #0f172a 0%, #1a2744 55%, #0f2a4a 100%);
  overflow: hidden;
}

.skeleton-shimmer {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.04) 50%,
    transparent 100%
  );
  background-size: 200% 100%;
  animation: shimmer 2.2s ease-in-out infinite;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.skeleton-fade-leave-active {
  transition: opacity 0.5s ease;
}
.skeleton-fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .skeleton-shimmer {
    animation: none;
  }
}

/* ══════════════════════════════════════════════════════
   Ads Carousel
══════════════════════════════════════════════════════ */
.ads-section {
  box-sizing: border-box;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 36px 48px 0;
}

.ads-wrap {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 1;
  max-height: 160px;
  border-radius: 16px;
  overflow: hidden;
  background: #0a1628;
}

/* ── Ads skeleton ───────────────────────── */
.ads-skeleton {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #1a2744 0%, #0f172a 100%);
  overflow: hidden;
}

/* ── Ads slides ─────────────────────────── */
.ads-track {
  position: absolute;
  inset: 0;
}

.ads-slide {
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity 700ms ease-in-out;
}

.ads-slide.is-active {
  opacity: 1;
  z-index: 2;
}

.ads-slide--clickable {
  cursor: pointer;
}

@media (prefers-reduced-motion: reduce) {
  .ads-slide {
    transition: none;
  }
}

.ads-slide__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

.ads-slide__gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to right,
    rgba(8, 12, 24, 0.65) 0%,
    rgba(8, 12, 24, 0.25) 50%,
    transparent 100%
  );
  pointer-events: none;
}

.ads-slide__content {
  position: absolute;
  top: 50%;
  left: 36px;
  transform: translateY(-50%);
  z-index: 3;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ads-slide__title {
  font-family: "Georgia", serif;
  font-size: clamp(1rem, 2vw, 1.35rem);
  font-weight: 400;
  color: #ffffff;
  letter-spacing: 0.03em;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.4);
  line-height: 1.3;
  max-width: 480px;
}

.ads-slide__cta {
  display: inline-flex;
  align-items: center;
  padding: 7px 18px;
  background: #eba301;
  color: #ffffff;
  text-decoration: none;
  font-family: "Plus Jakarta Sans", system-ui, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border-radius: 6px;
  width: fit-content;
  transition: background 0.2s ease, transform 0.2s ease;
}

.ads-slide__cta:hover {
  background: #d49200;
  transform: translateY(-1px);
}

/* ── Ads chevrons ───────────────────────── */
.ads-chevron {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(6px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease, background 0.2s ease;
  padding: 0;
}

.ads-wrap:hover .ads-chevron {
  opacity: 1;
}
.ads-chevron:hover {
  background: rgba(255, 255, 255, 0.28);
}
.ads-chevron--prev {
  left: 14px;
}
.ads-chevron--next {
  right: 14px;
}

/* ── Ads dots ───────────────────────────── */
.ads-dots {
  position: absolute;
  bottom: 12px;
  right: 18px;
  z-index: 10;
  display: flex;
  gap: 6px;
  align-items: center;
}

.ads-dot {
  height: 6px;
  width: 6px;
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.35);
  border: none;
  cursor: pointer;
  padding: 0;
  transition: width 0.3s ease, background 0.3s ease;
}

.ads-dot.is-active {
  width: 20px;
  background: #eba301;
}

/* ── Search bar ─────────────────────────── */
.hero-product-tabs {
  display: flex;
  align-items: center;
  gap: 26px;
  min-height: 28px;
  overflow: hidden;
  border-bottom: 1px solid #e6e0d6;
}

.hero-product-tabs::-webkit-scrollbar { display: none; }

.hero-product-tab {
  align-self: stretch;
  min-height: 28px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #5a5147;
  font-family: "Karla", sans-serif;
  font-size: 13px;
  font-weight: 400;
  white-space: nowrap;
  cursor: pointer;
  position: relative;
}

.hero-product-tab.is-active {
  color: #14110e;
  font-weight: 700;
}

.hero-product-tab.is-active::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px;
  height: 2px;
  background: #eba301;
}

.hero-product-tab:focus-visible {
  outline: 2px solid #eba301;
  outline-offset: 2px;
}

.search-bar-anchor {
  position: absolute;
  left: 50%;
  bottom: 28px;
  z-index: 20;
  width: min(calc(100% - 96px), 1349px);
  transform: translateX(-50%);
  box-sizing: border-box;
}

.search-panel {
  display: grid;
  gap: 14px;
  min-height: 143px;
  padding: 16px;
  box-sizing: border-box;
  background: #ffffff;
  border-radius: 5px;
  box-shadow: 0 2px 8px rgba(20, 17, 14, 0.08);
}

.search-fields-grid {
  display: grid;
  grid-template-columns: 251fr 352fr 251fr 251fr 131fr;
  gap: 20px;
  min-width: 0;
}

.search-field {
  display: grid;
  grid-template-rows: 11px 52px;
  gap: 6px;
  min-width: 0;
}

.search-field__label {
  font-family: "Karla", sans-serif;
  font-size: 11px;
  line-height: 11px;
  font-weight: 700;
  letter-spacing: 1.32px;
  text-transform: uppercase;
  color: #5a5147;
}

.search-control-wrap {
  position: relative;
  display: block;
  min-width: 0;
}

.search-control {
  width: 100%;
  height: 52px;
  box-sizing: border-box;
  padding: 0 36px 0 15px;
  border: 1px solid #d8d1c6;
  border-radius: 5px;
  outline: 0;
  background: #fff;
  color: #5a5147;
  font-family: "Karla", sans-serif;
  font-size: 15px;
  font-weight: 400;
  line-height: 15px;
  appearance: none;
  transition: border-color .16s ease, box-shadow .16s ease;
}

.search-field--zone .search-control {
  padding-right: 15px;
}

.search-control::placeholder {
  color: #5a5147;
  opacity: 1;
}

.search-control:focus {
  border-color: #eba301;
  box-shadow: 0 0 0 2px rgba(235, 163, 1, .16);
}

.search-control-chevron {
  position: absolute;
  right: 15px;
  top: 50%;
  color: #5a5147;
  font-size: 10px;
  line-height: 1;
  transform: translateY(-50%);
  pointer-events: none;
}

.search-cta {
  align-self: end;
  width: 100%;
  height: 52px;
  padding: 0 16px;
  background: #eba301;
  color: #14110e;
  border: none;
  border-radius: 5px;
  font-family: "Karla", sans-serif;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  white-space: nowrap;
}

.search-cta:hover {
  background: #d99500;
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(235, 163, 1, 0.3);
}

/* ══════════════════════════════════════════════════════
   Properties section
══════════════════════════════════════════════════════ */
.properties-section {
  box-sizing: border-box;
  width: min(100%, 1440px);
  margin: 0 auto;
  padding: 20px 48px 36px;
}

.properties-empty-state {
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e6e0d6;
  color: #756e65;
  background: #faf9f7;
  text-align: center;
}

.home-container {
  max-width: 1440px;
  margin: 0 auto;
}

.home-section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 20px;
}

.home-section-heading h2 {
  margin: 0;
  color: #14110e;
  font-family: "Sora", "Karla", sans-serif;
  font-size: 24px;
  line-height: 29px;
  font-weight: 700;
  letter-spacing: 0;
}

.featured-heading-actions,
.featured-slider-controls {
  display: flex;
  align-items: center;
}

.featured-heading-actions { gap: 12px; }
.featured-slider-controls { gap: 7px; }

.featured-slider-controls button {
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid #d8d1c6;
  border-radius: 50%;
  color: #14110e;
  background: #fff;
  font: 600 17px/1 "Karla", sans-serif;
  cursor: pointer;
  transition: border-color .2s ease, color .2s ease, background .2s ease;
}

.featured-slider-controls button:hover,
.featured-slider-controls button:focus-visible {
  border-color: #7b6134;
  color: #fff;
  background: #7b6134;
  outline: none;
}

.see-all-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 36px;
  padding: 0 14px;
  background: transparent;
  border: 1px solid #7b6134;
  color: #7b6134;
  border-radius: 10px;
  font-family: "Karla", sans-serif;
  font-size: 12px;
  line-height: 14px;
  font-weight: 600;
  letter-spacing: 0;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease,
    box-shadow 0.2s ease;
}

.see-all-btn:hover {
  background: #7b6134;
  color: #ffffff;
  transform: none;
  box-shadow: none;
}

.see-all-btn svg {
  transition: transform 0.2s ease;
}

.see-all-btn:hover svg {
  transform: translateX(4px);
}

/* ══════════════════════════════════════════════════════
   Property cards — frame 1:3
══════════════════════════════════════════════════════ */
.properties-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
}

.properties-grid--carousel {
  grid-template-columns: none;
  grid-auto-flow: column;
  grid-auto-columns: calc((100% - 60px) / 4);
  overflow-x: auto;
  overscroll-behavior-inline: contain;
  scroll-behavior: smooth;
  scroll-snap-type: inline mandatory;
  scrollbar-width: none;
}

.properties-grid--carousel::-webkit-scrollbar { display: none; }
.properties-grid--carousel .property-grid-item { scroll-snap-align: start; }

.property-grid-item { min-width: 0; }

.imovel-card {
  display: block;
  box-sizing: border-box;
  min-width: 0;
  height: 100%;
  min-height: 432px;
  border: 1px solid #e6e0d6;
  border-radius: 5px;
  background: #fff;
  color: #14110e;
  box-shadow: 0 2px 8px rgba(20, 17, 14, .08);
  overflow: hidden;
  cursor: pointer;
  outline: none;
  transition: box-shadow .2s ease, border-color .2s ease;
}

.imovel-card:hover {
  border-color: #c9c2b6;
  box-shadow: 0 4px 12px rgba(20, 17, 14, .12);
}

.imovel-card:focus-visible { box-shadow: 0 0 0 3px rgba(235, 163, 1, .45); }

.image-container {
  position: relative;
  aspect-ratio: 4 / 3;
  border-bottom: 1px solid #e6e0d6;
  background: #c9c2b6;
  overflow: hidden;
}

.property-card__image { width: 100%; height: 100%; }
.property-card__skeleton-image { width: 100%; aspect-ratio: 4 / 3; }

.tipo-negocio-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 6;
  padding: 5px 10px;
  border-radius: 999px;
  background: #14110e;
  color: #fff;
  font-family: "Karla", sans-serif;
  font-size: 11px;
  line-height: 14px;
  font-weight: 600;
}

.featured-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 5;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 999px;
  background: #fff;
  color: #14110e;
  font-family: "Karla", sans-serif;
  font-size: 11px;
  line-height: 14px;
  font-weight: 600;
  pointer-events: none;
  box-shadow: 0 1px 4px rgba(20, 17, 14, .12);
}

.featured-badge i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #eba301;
}

.property-card__photos {
  position: absolute;
  right: 10px;
  bottom: 10px;
  padding: 4px 7px;
  border-radius: 3px;
  background: #14110e;
  color: #fff;
  font-family: "Karla", sans-serif;
  font-size: 10px;
  line-height: 12px;
}

.property-card__content {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-height: 191px;
  padding: 14px 14px 18px;
}

.property-card__price-row,
.home-catalog-card__price {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.property-card__price-row strong,
.home-catalog-card__price strong {
  color: #14110e;
  font-family: "Sora", "Karla", sans-serif;
  font-size: 22px;
  line-height: 24.2px;
  font-weight: 700;
}

.property-card__price-row strong small,
.home-catalog-card__price strong small {
  font: inherit;
}

.property-card__favorite {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  box-sizing: border-box;
  border: 1px solid #e6e0d6;
  border-radius: 5px;
  color: #5a5147;
  background: #fff;
  font-family: "Karla", sans-serif;
  font-size: 22px;
}

.property-card__title {
  display: -webkit-box;
  margin: 6px 0 0;
  overflow: hidden;
  color: #14110e;
  font-family: "Karla", sans-serif;
  font-size: 15px;
  line-height: 19.5px;
  font-weight: 600;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  line-clamp: 1;
}

.property-card__location,
.home-catalog-card__location {
  margin: 2px 0 0;
  overflow: hidden;
  color: #5a5147;
  font-family: "Karla", sans-serif;
  font-size: 14px;
  line-height: 18.2px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.property-card__facts {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}

.property-card__facts span {
  padding: 5px 8px;
  border-radius: 3px;
  background: #f4f0e8;
  color: #5a5147;
  font-family: "Karla", sans-serif;
  font-size: 10px;
  line-height: 12px;
  white-space: nowrap;
}

.property-card__updated {
  display: block;
  margin-top: auto;
  padding-top: 12px;
  color: #8a8178;
  font-family: "Karla", sans-serif;
  font-size: 10px;
  line-height: 12px;
}

.q-img {
  transition: transform 0.3s ease;
}
.imovel-card:hover .q-img {
  transform: scale(1.025);
}

/* ══════════════════════════════════════════════════════
   Responsive
══════════════════════════════════════════════════════ */
@media (max-width: 768px) {
  .hero-static {
    height: clamp(250px, 62vw, 380px);
  }

  .search-bar-anchor {
    position: relative;
    left: auto;
    bottom: auto;
    width: calc(100% - 32px);
    margin: -24px auto 0;
    transform: none;
  }

  .hero-product-tabs {
    gap: 22px;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
  }

  .hero-product-tab {
    flex: 0 0 auto;
  }

  .search-panel {
    gap: 16px;
    height: auto;
    min-height: 0;
    border-radius: 5px;
  }

  .search-fields-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .search-cta {
    height: 52px;
    border-radius: 5px;
    background: #eba301 !important;
    color: #14110e !important;
  }

  .properties-section {
    padding: 16px 16px 48px;
  }

  .properties-grid { grid-template-columns: 1fr; }
  .properties-grid--carousel {
    grid-template-columns: none;
    grid-auto-columns: calc(100% - 36px);
  }

  .ads-section {
    padding: 16px 16px 0;
  }
  .ads-wrap {
    border-radius: 12px;
  }
  .ads-slide__content {
    left: 20px;
    gap: 8px;
  }
}

@media (min-width: 769px) and (max-width: 1100px) {
  .hero-static { height: 360px; }
  .search-bar-anchor {
    position: relative;
    left: auto;
    bottom: auto;
    width: calc(100% - 48px);
    margin: -32px auto 0;
    transform: none;
  }
  .search-panel { min-height: 0; }
  .search-fields-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .search-cta { grid-column: 1 / -1; }
  .properties-section { padding-inline: 24px; }
  .properties-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .properties-grid--carousel {
    grid-template-columns: none;
    grid-auto-columns: calc((100% - 20px) / 2);
  }
  .home-carousel-placement { width: min(calc(100% - 48px), 970px); }
  .home-catalog-section .home-container { padding-inline: 24px; }
}

/* ══════════════════════════════════════════════════════
   Why Us
══════════════════════════════════════════════════════ */
@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700&display=swap");

.why-section {
  background: #f8fafc;
  padding: 96px 2rem;
}

.why-container {
  max-width: 1200px;
  margin: 0 auto;
}

.why-header {
  text-align: center;
  margin-bottom: 64px;
}

.why-eyebrow {
  display: inline-block;
  font-family: "Plus Jakarta Sans", system-ui, sans-serif;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #eba301;
  margin-bottom: 14px;
}

.why-title {
  font-family: "Georgia", "Palatino Linotype", serif;
  font-size: clamp(1.9rem, 3.5vw, 2.75rem);
  font-weight: 400;
  color: #0f172a;
  margin: 0 0 16px;
  line-height: 1.2;
}

.why-sub {
  font-family: "Plus Jakarta Sans", system-ui, sans-serif;
  font-size: 1rem;
  color: #64748b;
  max-width: 540px;
  margin: 0 auto;
  line-height: 1.7;
}

.why-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 28px;
}

.why-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 36px 28px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.why-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 20px 48px rgba(0, 0, 0, 0.09);
}

.why-icon {
  width: 52px;
  height: 52px;
  border-radius: 13px;
  background: color-mix(in srgb, var(--accent) 10%, transparent);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 22px;
}

.why-card-title {
  font-family: "Plus Jakarta Sans", system-ui, sans-serif;
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 10px;
  line-height: 1.3;
}

.why-card-body {
  font-family: "Plus Jakarta Sans", system-ui, sans-serif;
  font-size: 0.875rem;
  color: #64748b;
  line-height: 1.7;
  margin: 0;
}

@media (max-width: 1024px) {
  .why-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .why-section {
    padding: 64px 1.25rem;
  }
  .why-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  .why-header {
    margin-bottom: 40px;
  }
}

/* ── Advertise button ───────────────────────────── */

.advertise-container {
  display: flex;
  justify-content: flex-start;
  box-sizing: border-box;
  max-width: 1440px;
  margin: 0 auto;
  padding: 20px 48px 2px;
}

.advertise-btn {
  background: #eba301;
  color: white;
  border: none;
  padding: 10px 26px;
  border-radius: 8px;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  letter-spacing: 0.03em;
  transition: all 0.25s ease;
}

.advertise-btn:hover {
  background: #d49200;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(235, 163, 1, 0.3);
}

@media (max-width: 768px) {
  .advertise-container {
    padding: 16px 16px 2px;
  }
}
.home-catalog-section { padding: 0; background: #fff; }
.why-section { padding: 36px 0 72px; background: #fff; }
.why-container { box-sizing: border-box; width: min(100%, 1440px); max-width: 1440px; padding: 0 48px; }
.why-header { margin-bottom: 24px; text-align: left; }
.why-title { margin-bottom: 8px; font-family: "Plus Jakarta Sans", system-ui, sans-serif; font-size: 24px; font-weight: 650; color: #14110e; }
.why-sub { max-width: 760px; margin: 0; font-size: 13px; color: #756e65; }
.why-grid { gap: 20px; }
.why-card { min-height: 150px; padding: 20px; border: 0; border-radius: 0; background: #f4f0e8; box-shadow: none; }
.why-card:hover { transform: none; box-shadow: none; }
.why-icon { width: 34px; height: 34px; margin-bottom: 15px; border-radius: 0; color: #14110e; background: transparent; }
.why-card-title { margin-bottom: 6px; color: #14110e; }
.why-card-body { font-size: 12px; line-height: 1.5; color: #6f685f; }
.home-catalog-section .home-container { box-sizing: border-box; width: min(100%, 1440px); padding: 36px 48px 0; }
.advertise-container, .ads-section { display: none; }
.home-top-ad { width: min(100%, 1440px); margin: 18px auto 0; }
.home-carousel-placement { width: min(calc(100% - 96px), 970px); margin: 36px auto 0; }
.home-carousel-placement + .home-catalog-section { margin-top: 19px; }
.home-catalog-heading { display: flex; align-items: center; justify-content: space-between; gap: 20px; margin-bottom: 18px; }
.home-catalog-heading h2 { margin: 0; color: #14110e; font-family: "Sora", "Karla", sans-serif; font-size: 24px; line-height: 29px; font-weight: 700; letter-spacing: 0; }
.home-catalog-heading .featured-heading-actions > button { display: inline-flex; align-items: center; justify-content: center; min-height: 36px; padding: 0 14px; border: 1px solid #7b6134; border-radius: 10px; color: #7b6134; background: transparent; font-family: "Karla", sans-serif; font-size: 12px; line-height: 14px; font-weight: 600; white-space: nowrap; cursor: pointer; transition: color .2s ease, background .2s ease; }
.home-catalog-heading .featured-heading-actions > button:hover { color: #fff; background: #7b6134; }
.home-catalog-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 22px; }
.home-catalog-grid--carousel { grid-template-columns:none; grid-auto-flow:column; grid-auto-columns:calc((100% - 66px) / 4); overflow-x:auto; overscroll-behavior-inline:contain; scroll-behavior:smooth; scroll-snap-type:inline mandatory; scrollbar-width:none; }
.home-catalog-grid--carousel::-webkit-scrollbar { display:none; }
.home-catalog-grid--carousel .home-catalog-card { scroll-snap-align:start; }
.home-catalog-state { min-height: 180px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; border: 1px solid #e6e0d6; color: #756e65; background: #faf9f7; text-align: center; }
.home-catalog-state strong { color: #14110e; font-family: "Karla", sans-serif; font-size: 15px; }
.home-catalog-state span { max-width: 520px; font-size: 12px; }
.home-catalog-card { min-width: 0; min-height: 432px; border: 1px solid #e6e0d6; border-radius: 5px; color: #14110e; background: #fff; box-shadow: 0 2px 8px rgba(20,17,14,.08); outline: none; cursor: pointer; overflow: hidden; transition: border-color .2s ease, box-shadow .2s ease; }
.home-catalog-card:hover { border-color: #c9c2b6; box-shadow: 0 4px 12px rgba(20,17,14,.12); }
.home-catalog-card:focus-visible { box-shadow: 0 0 0 3px #eba301; }
.home-catalog-card__image { position: relative; aspect-ratio: 4 / 3; border-bottom: 1px solid #e6e0d6; overflow: hidden; background: #c9c2b6; }
.home-catalog-card__image img { width: 100%; height: 100%; display: block; object-fit: cover; transition: transform .3s ease; }
.home-catalog-card:hover img { transform: scale(1.025); }
.home-catalog-card__image > span { position: absolute; left: 12px; top: 12px; padding: 5px 10px; border-radius: 999px; color: #fff; background: #14110e; font-family: "Karla", sans-serif; font-size: 11px; line-height: 14px; font-weight: 600; }
.home-catalog-card__image > .home-catalog-card__promotion { right: 12px; left: auto; color: #14110e; background: #fff; box-shadow: 0 1px 4px rgba(20,17,14,.14); }
.home-catalog-card__content { box-sizing: border-box; min-height: 191px; padding: 14px 14px 18px; }
.home-catalog-card__price { min-height: 40px; }
.home-catalog-card h3 { display: -webkit-box; margin: 6px 0 0; overflow: hidden; color: #14110e; font-family: "Karla", sans-serif; font-size: 15px; line-height: 19.5px; font-weight: 600; -webkit-box-orient: vertical; -webkit-line-clamp: 1; line-clamp: 1; }
.home-wide-ad { color: white; background: linear-gradient(100deg, #203c38, #466a60 62%, #14110e); }
.home-wide-ad .home-container { min-height: 190px; display: flex; align-items: center; justify-content: space-between; gap: 32px; }
.home-wide-ad small { opacity: .68; letter-spacing: .14em; }
.home-wide-ad h3 { margin: 8px 0 0; font-size: clamp(24px, 3vw, 40px); }
.home-wide-ad button { min-width: 160px; min-height: 48px; border: 1px solid rgba(255,255,255,.7); color: white; background: transparent; cursor: pointer; }

@media (max-width: 1024px) {
  .home-catalog-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 34px 20px; }
  .home-catalog-grid--carousel { grid-template-columns:none; grid-auto-columns:calc((100% - 20px) / 2); }
}

@media (max-width: 600px) {
  .hero-copy { top: 62px; width: calc(100% - 32px); }
  .hero-copy p { margin-bottom: 8px; }
  .hero-copy h1 { font-size: 28px; }
  .home-carousel-placement { width: calc(100% - 32px); margin-top: 24px; }
  .home-carousel-placement + .home-catalog-section { margin-top: 8px; }
  .home-catalog-section { padding: 0; }
  .home-catalog-section .home-container { padding: 28px 16px 0; }
  .why-section { padding: 40px 0 56px; }
  .why-container { padding: 0 16px; }
  .home-section-heading,
  .home-catalog-heading { align-items: flex-start; flex-direction: column; gap: 12px; }
  .featured-heading-actions { width: 100%; justify-content: space-between; }
  .home-section-heading h2,
  .home-catalog-heading h2 { font-size: 20px; line-height: 25px; }
  .see-all-btn,
  .home-catalog-heading .featured-heading-actions > button { min-height: 34px; padding-inline: 11px; font-size: 11px; }
  .home-catalog-grid { grid-template-columns: 1fr; }
  .home-catalog-grid--carousel { grid-template-columns:none; grid-auto-columns:calc(100% - 36px); gap:20px; }
  .home-catalog-card h3 { min-height: 0; }
  .home-wide-ad .home-container { min-height: 260px; padding-block: 38px; align-items: flex-start; flex-direction: column; justify-content: center; }
  .home-wide-ad button { width: 100%; }
}
</style>
